import JSZip from "jszip";
import type { Annotations, FlightMeta, PanoramaWithUrl } from "../api/types";
import { buildKmlDocument } from "./kml";
import { collectImages } from "./images";
import { fetchTrack } from "./track";

export interface BuildKmzInput {
  meta: FlightMeta;
  annotations: Annotations;
  trackUrl: string;
  panoramas: PanoramaWithUrl[];
  frameUrl: (frameId: string, variant: "full" | "thumb") => Promise<string>;
}

export interface BuildKmzResult {
  blob: Blob;
  failures: number;
}

/** Derive a filesystem-safe `.kmz` filename from the flight metadata. */
export function kmzFilename(meta: FlightMeta): string {
  const safe = meta.name.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return `${safe}-${meta.captureDate}.kmz`;
}

/** Build an annotated KMZ for a flight. Never throws on individual image
 *  failures — those are surfaced via `failures`. */
export async function buildKmz(input: BuildKmzInput): Promise<BuildKmzResult> {
  const zip = new JSZip();
  const track = await fetchTrack(input.trackUrl);
  // A frame linked to a POI is already represented by that POI's placemark —
  // skip it from the Saved Frames folder so the KMZ doesn't carry a duplicate
  // pin. The linked frame's full-res image is still pulled into the POI
  // balloon's "open full size" link via poiLinkedFrames below.
  const standaloneFrames = input.annotations.frames.filter(
    (f) => f.poiId === undefined,
  );
  const poiLinkedFrames = input.annotations.frames.filter(
    (f) => f.poiId !== undefined,
  );
  const { hrefs, failures } = await collectImages(zip, {
    pois: input.annotations.pois,
    frames: standaloneFrames,
    poiLinkedFrames,
    panoramas: input.panoramas,
    frameUrl: input.frameUrl,
  });
  const kml = buildKmlDocument({
    flightName: input.meta.name,
    flightId: input.meta.flightId,
    captureDate: input.meta.captureDate,
    videoStartUtc: input.meta.videoStartUtc,
    track,
    pins: input.annotations.pins,
    pois: input.annotations.pois,
    frames: standaloneFrames,
    panoramas: input.panoramas,
    imageHrefs: hrefs,
  });
  zip.file("doc.kml", kml);
  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
  return { blob, failures };
}
